import { capitalize } from 'vue';

import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
import rootState from '@/store/state';
import i18n from '@/vueI18n';

import { Option } from '..';

export enum ReportDefinitionOptionsFilterValues {
  MediaOwner = 'MEDIA_OWNER',
  Buyer = 'BUYER',
  Advertiser = 'ADVERTISER',
  City = 'CITY',
  Country = 'COUNTRY',
  Deal = 'DEAL',
  DealID = 'DEAL_ID',
  RevenueSource = 'REVENUE_SOURCE',
  BuyingPlatform = 'BUYING_PLATFORM',
  MediaType = 'MEDIA_TYPE',
  MediaTypeParent = 'MEDIA_TYPE_PARENT',
  MediaTypeChild = 'MEDIA_TYPE_CHILD',
  MediaTypeGrandchild = 'MEDIA_TYPE_GRANDCHILD',
  Region = 'REGION',
  Site = 'SITE',
  Unit = 'UNIT',
  Market = 'MARKET',
  CuratedDealID = 'CURATED_DEAL_ID',
  CuratedDeal = 'CURATED_DEAL',
  Account = 'ACCOUNT',
  Creative = 'CREATIVE',
  CreativeFile = 'CREATIVE_FILE',
  Network = 'NETWORK',
  Campaign = 'CAMPAIGN',
  LineItem = 'LINE_ITEM',
  CreativeFileWidth = 'CREATIVE_FILE_WIDTH',
  CreativeFileHeight = 'CREATIVE_FILE_HEIGHT',
}

export default class ReportDefinitionOptions {
  static getLocalizedOptionName(name) {
    return capitalize(i18n.global.t(`options.reportDefinition.${name}`));
  }

  static breakdownOptions(): Option[] {
    return [
      Option.fromObject({
        value: 'HOURLY',
        name: ReportDefinitionOptions.getLocalizedOptionName('breakdown.hourly'),
        sort: 1,
      }),
      Option.fromObject({
        value: 'DAILY',
        name: ReportDefinitionOptions.getLocalizedOptionName('breakdown.daily'),
        sort: 2,
      }),
      Option.fromObject({
        value: 'WEEKLY_MONDAY',
        name: ReportDefinitionOptions.getLocalizedOptionName('breakdown.weekly'),
        sort: 3,
      }),
      Option.fromObject({
        value: 'MONTHLY',
        name: ReportDefinitionOptions.getLocalizedOptionName('breakdown.monthly'),
        sort: 4,
      }),
      Option.fromObject({
        value: 'CUMULATIVE',
        name: ReportDefinitionOptions.getLocalizedOptionName('breakdown.cumulative'),
        sort: 5,
      }),
    ];
  }

  static formatOptions() {
    return [
      Option.fromObject({
        value: 'csv',
        name: ReportDefinitionOptions.getLocalizedOptionName('format.csv'),
      }),
      Option.fromObject({
        value: 'xlsx',
        name: ReportDefinitionOptions.getLocalizedOptionName('format.xlsx'),
      }),
      Option.fromObject({
        value: 'xlsx_styled',
        name: ReportDefinitionOptions.getLocalizedOptionName('format.xlsx_styled'),
      }),
    ];
  }

  static dateOptions() {
    return [
      Option.fromObject({
        value: 'ABSOLUTE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.custom'),
      }),
      ...ReportDefinitionOptions.dateRangeOptions(),
    ];
  }

  static dateRangeOptions() {
    return [
      Option.fromObject({
        value: 'YESTERDAY',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.yesterday'),
      }),
      Option.fromObject({
        value: 'LAST_WEEK',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.lastWeek'),
      }),
      Option.fromObject({
        value: 'LAST_MONTH',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.lastMonth'),
      }),
      Option.fromObject({
        value: 'PREVIOUS_7_DAYS',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.last7Days'),
      }),
      Option.fromObject({
        value: 'PREVIOUS_30_DAYS',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.last30Days'),
      }),
      Option.fromObject({
        value: 'PREVIOUS_60_DAYS',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.last60Days'),
      }),
      Option.fromObject({
        value: 'PREVIOUS_90_DAYS',
        name: ReportDefinitionOptions.getLocalizedOptionName('dateRange.last90Days'),
      }),
    ];
  }

  static allDimensionOptions() {
    const reportDimensionsForPlans = [
      Option.fromObject({
        value: 'PLAN_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planID'),
      }),
      Option.fromObject({
        value: 'PLAN_NAME',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planName'),
      }),
      Option.fromObject({
        value: 'PLAN_BUDGET',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planBudget'),
      }),
      Option.fromObject({
        value: 'PLAN_IMPRESSIONS',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planImpressions'),
      }),
      Option.fromObject({
        value: 'PLAN_START_DATE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planStartDate'),
      }),
      Option.fromObject({
        value: 'PLAN_END_DATE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planEndDate'),
      }),
      Option.fromObject({
        value: 'PLAN_CPM',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planCPM'),
      }),
      Option.fromObject({
        value: 'PLAN_CPM_MARKUP',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.planCPMMarkup'),
      }),
    ];
    const reportDimensionsForCampaigns = rootState?.orgApp?.organization?.isSidekickOrganization
      ? []
      : [
          Option.fromObject({
            value: 'CAMPAIGN',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaign'),
          }),
          Option.fromObject({
            value: 'CAMPAIGN_ID',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaignID'),
          }),
          Option.fromObject({
            value: 'CAMPAIGN_CUSTOM_FIELDS',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaignCustomFields'),
          }),
          Option.fromObject({
            value: 'CAMPAIGN_START_DATE',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaignStartDate'),
          }),
          Option.fromObject({
            value: 'CAMPAIGN_END_DATE',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaignEndDate'),
          }),
          Option.fromObject({
            value: 'CAMPAIGN_BUDGET',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaignBudget'),
          }),
        ];
    const reportDimensionsForLineItems = rootState?.orgApp?.organization?.isSidekickOrganization
      ? []
      : [
          Option.fromObject({
            value: 'LINE_ITEM',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.lineItem'),
          }),
          Option.fromObject({
            value: 'LINE_ITEM_ID',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.lineItemID'),
          }),
          Option.fromObject({
            value: 'LINE_ITEM_FLIGHT_FROM',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.lineItemStartDate'),
          }),
          Option.fromObject({
            value: 'LINE_ITEM_FLIGHT_TO',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.lineItemEndDate'),
          }),
          Option.fromObject({
            value: 'LINE_ITEM_BUDGET',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.lineItemBudget'),
          }),
          Option.fromObject({
            value: 'LINE_ITEM_CUSTOM_FIELDS',
            name: ReportDefinitionOptions.getLocalizedOptionName('dimension.lineItemCustomFields'),
          }),
        ];
    const reportDimensionsForNetworks = [
      Option.fromObject({
        value: 'NETWORK',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.network'),
      }),
      Option.fromObject({
        value: 'NETWORK_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.networkID'),
      }),
    ];
    const reportDimensionsForCreatives = [
      Option.fromObject({
        value: 'CREATIVE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.creative'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'CREATIVE_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.creativeID'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'CREATIVE_FILE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.creativeFile'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'CREATIVE_FILE_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.creativeFileID'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'CREATIVE_FILE_WIDTH',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.width'),
      }),
      Option.fromObject({
        value: 'CREATIVE_FILE_HEIGHT',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.height'),
      }),
      Option.fromObject({
        value: 'CREATIVE_FILE_DIMENSIONS',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.dimensionsWxH'),
      }),
    ];
    return [
      Option.fromObject({
        value: 'BUYER',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.buyer'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'BUYER_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.buyerID'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'MARKET',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.market'),
      }),
      Option.fromObject({
        value: 'MEDIA_OWNER',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.mediaOwner'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'MEDIA_OWNER_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.mediaOwnerID'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'ADVERTISER',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.advertiser'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'ADVERTISER_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.advertiserID'),
        allowedWithFees: false,
      }),
      Option.fromObject({
        value: 'AUDIENCE_NAME',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.audienceName'),
      }),
      Option.fromObject({
        value: 'AUDIENCE_CODE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.audienceCode'),
      }),
      Option.fromObject({
        value: 'CUSTOM_AUDIENCE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.customAudiences'),
      }),
      Option.fromObject({
        value: 'DEAL',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.deal'),
      }),
      Option.fromObject({
        value: 'DEAL_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.dealID'),
      }),
      Option.fromObject({
        value: 'BUYING_PLATFORM',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.buyingPlatform'),
      }),
      Option.fromObject({
        value: 'MEDIA_TYPE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.venueType'),
      }),
      Option.fromObject({
        value: 'MEDIA_TYPE_PARENT',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.parentVenueType'),
      }),
      Option.fromObject({
        value: 'MEDIA_TYPE_CHILD',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.childVenueType'),
      }),
      Option.fromObject({
        value: 'MEDIA_TYPE_GRANDCHILD',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.grandchildVenueType'),
      }),
      Option.fromObject({
        value: 'COUNTRY',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.country'),
      }),
      Option.fromObject({
        value: 'REGION',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.region'),
      }),
      Option.fromObject({
        value: 'CITY',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.city'),
      }),
      Option.fromObject({
        value: 'SITE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.site'),
      }),
      Option.fromObject({
        value: 'SITE_EXTERNAL_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.siteExternalID'),
      }),
      Option.fromObject({
        value: 'TIMEZONE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.timezone'),
      }),
      Option.fromObject({
        value: 'UNIT',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unit'),
      }),
      Option.fromObject({
        value: 'UNIT_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unitID'),
      }),
      Option.fromObject({
        value: 'UNIT_EXTERNAL_ID',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unitExternalID'),
      }),
      Option.fromObject({
        value: 'SPOT_LENGTH',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.spotLength'),
      }),
      Option.fromObject({
        value: 'UNIT_ZIP_CODE',
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unitZipCode'),
      }),
      ...(rootState.orgApp.organizationSettings.uiHideLatitudeLongitude
        ? []
        : [
            Option.fromObject({
              value: 'UNIT_LATITUDE',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unitLatitude'),
            }),
          ]),
      ...(rootState.orgApp.organizationSettings.uiHideLatitudeLongitude
        ? []
        : [
            Option.fromObject({
              value: 'UNIT_LONGITUDE',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unitLongitude'),
            }),
          ]),
      ...(rootState.orgApp.organizationSettings.uiEnableDirectBilling
        ? [
            Option.fromObject({
              value: 'DIRECT_BILLING',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.directBilling'),
            }),
          ]
        : []),
      ...(rootState?.orgApp?.organizationSettings?.uiEnableAppDomainID
        ? [
            Option.fromObject({
              value: 'APP_DOMAIN_ID',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.appDomain'),
            }),
          ]
        : []),
      ...(rootState.orgApp.organizationSettings.uiEnableInsertionOrderBudget &&
      !rootState?.orgApp?.organization?.isSidekickOrganization
        ? [
            Option.fromObject({
              value: 'CAMPAIGN_INSERTION_ORDER_BUDGET',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaignInsertionOrderBudget'),
            }),
          ]
        : []),
      ...(rootState?.orgApp?.organization?.isDemandOrganization ||
      rootState?.orgApp?.organization?.isCuratorOrganization
        ? []
        : [
            Option.fromObject({
              value: 'DEAL_CUSTOM_FIELDS',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.dealCustomFields'),
            }),
            Option.fromObject({
              value: 'SCREEN_CUSTOM_FIELDS',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unitCustomFields'),
            }),
          ]),
      ...(rootState?.orgApp.organization?.isCuratorOrganization
        ? [
            Option.fromObject({
              value: 'CURATED_DEAL',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.curatedDeal'),
            }),
            Option.fromObject({
              value: 'CURATED_DEAL_ID',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.curatedDealId'),
            }),
          ]
        : [
            ...reportDimensionsForCampaigns,
            ...reportDimensionsForLineItems,
            ...reportDimensionsForNetworks,
            ...reportDimensionsForCreatives,
            Option.fromObject({
              value: 'ACCOUNT',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.account'),
            }),
            Option.fromObject({
              value: 'ACCOUNT_ID',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.accountID'),
            }),
            Option.fromObject({
              value: 'SSP',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.SSP'),
            }),
            Option.fromObject({
              value: 'REVENUE_SOURCE',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.revenueSource'),
              allowedWithFees: false,
            }),
            Option.fromObject({
              value: 'PURCHASE_TYPE',
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.purchaseType'),
            }),
          ]),
      // TODO : Enable CADENCE dimension when BE is ready
      // ...(rootState.orgApp.organizationSettings.uiEnableCadenceBuys
      //   ? [Option.fromObject({
      //     value: 'CADENCE',
      //     name: ReportDefinitionOptions.getLocalizedOptionName('dimension.cadence'),
      //   })]
      //   : []),
      ...(rootState?.orgApp?.organizationSettings?.enablePlansFeature ? reportDimensionsForPlans : []),
    ];
  }

  static dimensionOptions(isDemandOrganization, includeUnitDimensions = true) {
    let options = this.allDimensionOptions();
    if (isDemandOrganization) {
      options = options.filter((o) => o.value !== 'BUYER' && o.value !== 'BUYER_ID');
    } else if (rootState?.orgApp?.organization?.isCuratorOrganization) {
      options = options.filter((o) => o.value !== 'MEDIA_OWNER_ID');
    } else {
      options = options.filter((o) => o.value !== 'MEDIA_OWNER' && o.value !== 'MEDIA_OWNER_ID');
    }

    if (!includeUnitDimensions) {
      options = options.filter(
        (o) =>
          o.value !== 'UNIT' &&
          o.value !== 'UNIT_EXTERNAL_ID' &&
          o.value !== 'UNIT_LATITUDE' &&
          o.value !== 'UNIT_LONGITUDE' &&
          o.value !== 'UNIT_ID',
      );
    }
    return options;
  }

  static allFilterOptions() {
    return [
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.MediaOwner,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.mediaOwner'),
        validatorName: 'mediaOwnerFilter',
        allowedWithFees: false,
        displayField: 'idAndName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Buyer,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.buyer'),
        validatorName: 'buyerFilter',
        allowedWithFees: false,
        displayField: 'idAndName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Advertiser,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.advertiser'),
        options: [],
        selectedOptions: [],
        validatorName: 'advertiserFilter',
        allowedWithFees: false,
        displayField: 'idAndName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.City,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.city'),
        options: [],
        selectedOptions: [],
        validatorName: 'cityFilter',
        placeholder: i18n.global.t('messages.startTypingToSearch_3charactersMinimum'),
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Country,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.country'),
        options: [],
        selectedOptions: [],
        validatorName: 'countryFilter',
        placeholder: i18n.global.t('messages.startTypingToSearch'),
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Deal,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.deal'),
        options: [],
        selectedOptions: [],
        validatorName: 'dealFilter',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.DealID,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.dealID'),
        options: [],
        selectedOptions: [],
        validatorName: 'dealIdFilter',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.RevenueSource,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.revenueSource'),
        options: [],
        allowedWithFees: false,
        selectedOptions: [],
        validatorName: 'revenueSourceFilter',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.BuyingPlatform,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.buyingPlatform'),
        options: [],
        selectedOptions: [],
        validatorName: 'buyingPlatformFilter',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.MediaType,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.venueType'),
        options: [],
        selectedOptions: [],
        validatorName: 'mediaTypeFilter',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.MediaTypeParent,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.parentVenueType'),
        options: [],
        selectedOptions: [],
        validatorName: 'mediaTypeFilter',
        displayField: 'displayName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.MediaTypeChild,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.childVenueType'),
        options: [],
        selectedOptions: [],
        validatorName: 'mediaTypeFilter',
        displayField: 'displayName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.MediaTypeGrandchild,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.grandchildVenueType'),
        options: [],
        selectedOptions: [],
        validatorName: 'mediaTypeFilter',
        displayField: 'displayName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Region,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.region'),
        options: [],
        selectedOptions: [],
        validatorName: 'regionFilter',
        placeholder: i18n.global.t('messages.startTypingToSearch'),
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Site,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.site'),
        options: [],
        selectedOptions: [],
        validatorName: 'siteFilter',
        placeholder: i18n.global.t('messages.startTypingToSearch'),
        displayField: 'idAndName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Unit,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.unit'),
        options: [],
        selectedOptions: [],
        validatorName: 'unitFilter',
        placeholder: i18n.global.t('messages.startTypingToSearch'),
        displayField: 'idAndName',
      }),
      Option.fromObject({
        value: ReportDefinitionOptionsFilterValues.Market,
        name: ReportDefinitionOptions.getLocalizedOptionName('dimension.market'),
        options: [],
        selectedOptions: [],
        validatorName: 'marketFilter',
      }),
      ...(rootState?.orgApp.organization?.isCuratorOrganization
        ? [
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.CuratedDealID,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.curatedDealId'),
              options: [],
              selectedOptions: [],
              validatorName: 'curatedDealIdFilter',
            }),
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.CuratedDeal,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.curatedDeal'),
              options: [],
              selectedOptions: [],
              validatorName: 'curatedDealFilter',
            }),
          ]
        : [
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.Account,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.account'),
              options: [],
              selectedOptions: [],
              validatorName: 'accountFilter',
              displayField: 'idAndName',
            }),
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.Creative,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.creative'),
              options: [],
              selectedOptions: [],
              validatorName: 'creativeFilter',
              allowedWithFees: false,
              displayField: 'idAndName',
            }),
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.CreativeFile,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.creativeFile'),
              options: [],
              selectedOptions: [],
              validatorName: 'creativeFileFilter',
              allowedWithFees: false,
              displayField: 'idAndName',
            }),
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.CreativeFileWidth,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.width'),
              options: [],
              selectedOptions: [],
              validatorName: 'creativeFileWidthFilter',
            }),
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.CreativeFileHeight,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.height'),
              options: [],
              selectedOptions: [],
              validatorName: 'creativeFileHeightFilter',
            }),
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.Network,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.network'),
              options: [],
              selectedOptions: [],
              validatorName: 'networkFilter',
              displayField: 'idAndName',
            }),
          ]),
      ...(rootState?.orgApp?.organization?.isSidekickOrganization
        ? []
        : [
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.Campaign,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.campaign'),
              options: [],
              selectedOptions: [],
              validatorName: 'campaignFilter',
              displayField: 'displayName',
            }),
            Option.fromObject({
              value: ReportDefinitionOptionsFilterValues.LineItem,
              name: ReportDefinitionOptions.getLocalizedOptionName('dimension.lineItem'),
              options: [],
              selectedOptions: [],
              validatorName: 'lineItemFilter',
              displayField: 'displayName',
            }),
          ]),
    ];
  }

  static filterOptions(organizationType, includeUnitFilters = true) {
    let options = this.allFilterOptions();

    if (organizationType === OrganizationType.DEMAND) {
      options = options.filter((o) => o.value !== 'BUYER');
    } else {
      options = options.filter((o) => o.value !== 'MEDIA_OWNER');
    }

    if (!includeUnitFilters) {
      options = options.filter((o) => o.value !== 'UNIT');
    }

    return options;
  }

  static allMetricOptions() {
    return [
      Option.fromObject({
        value: 'PLAYS',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.plays'),
      }),
      Option.fromObject({
        value: 'AUDIENCE_CPM',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.audienceCPM'),
      }),
      Option.fromObject({
        value: 'IMPRESSIONS',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.impressions'),
      }),
      Option.fromObject({
        value: 'ADVERTISER_CPM',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.advertiserCPM'),
      }),
      Option.fromObject({
        value: 'ADVERTISER_SPEND',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.advertiserSpend'),
      }),
      Option.fromObject({
        value: 'AUDIENCE_IMPRESSIONS',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.audienceImpressions'),
      }),
      Option.fromObject({
        value: 'CONCENTRATION',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.concentration'),
      }),
      Option.fromObject({
        value: 'SPEND',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.spend'),
      }),
      Option.fromObject({
        value: 'CPM',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.cpm'),
      }),
      Option.fromObject({
        value: 'FEES',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.fees'),
      }),
      Option.fromObject({
        value: 'ISBA_NET_REACH_FREQUENCY',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.netReachFrequency'),
      }),
      Option.fromObject({
        value: 'EXTRA_COSTS',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.extraCosts'),
      }),
      Option.fromObject({
        value: 'PROGRESS',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.progress'),
      }),
      Option.fromObject({
        value: 'PACING_VS_EXPECTED',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.pacingVsExpected'),
      }),
      Option.fromObject({
        value: 'PACING_VS_EVEN',
        name: ReportDefinitionOptions.getLocalizedOptionName('metric.pacingVsEven'),
      }),
      ...(rootState?.orgApp?.organization?.isSupplyOrganization
        ? [
            Option.fromObject({
              value: 'OWNER_NET_REVENUE',
              name: ReportDefinitionOptions.getLocalizedOptionName('metric.ownerNetRevenue'),
            }),
          ]
        : []),
      ...(rootState?.orgApp?.organization?.isCuratorOrganization
        ? [
            Option.fromObject({
              value: 'CURATOR_PRICE',
              name: ReportDefinitionOptions.getLocalizedOptionName('metric.curatorPrice'),
            }),
          ]
        : []),
      ...(rootState.orgApp?.organization?.isDemandOrganization
        ? [
            Option.fromObject({
              value: 'MARGINS',
              name: ReportDefinitionOptions.getLocalizedOptionName('metric.margin'),
            }),
          ]
        : []),
      ...(rootState?.orgApp?.organization?.isDemandOrganization
        ? [
            Option.fromObject({
              value: 'BUYER_NET_SPEND',
              name: ReportDefinitionOptions.getLocalizedOptionName('metric.buyerNetSpend'),
            }),
          ]
        : []),
    ];
  }

  static metricOptions(orgSettings) {
    let options = this.allMetricOptions();
    if (!orgSettings?.uiEnableAudienceCPM) {
      options = options.filter((o) => o.value !== 'AUDIENCE_CPM');
    }
    if (!orgSettings?.reportPostProcessIsbaNetReachFrequency) {
      options = options.filter((o) => o.value !== 'ISBA_NET_REACH_FREQUENCY');
    }
    if (orgSettings?.uiHideReportFees) {
      options = options.filter(
        (o) => o.value !== 'FEES' && o.value !== 'OWNER_NET_REVENUE' && o.value !== 'BUYER_NET_SPEND',
      );
    }
    if (!orgSettings?.uiEnableReportDefinitionMetricForPlays) {
      options = options.filter((o) => o.value !== 'PLAYS');
    }
    if (!orgSettings?.uiEnableAgencyMargins && !orgSettings?.uiEnableAgencyBudgetMargins) {
      options = options.filter((o) => o.value !== 'ADVERTISER_SPEND' && o.value !== 'ADVERTISER_CPM');
    }
    return options;
  }
}
